// client/src/components/admin/AdminPatientAdd.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import adminService from '../../services/adminService';
import '../../styles/components/admin/AdminPatientDetails.css';

const AdminPatientAdd = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [tenantLoading, setTenantLoading] = useState(true);

  // Form data - only essential fields
  const [formData, setFormData] = useState({
    tenantId: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  // Validation errors
  const [validationErrors, setValidationErrors] = useState({});

  // Fetch tenants on component mount
  useEffect(() => {
    const fetchTenants = async () => {
      try {
        setTenantLoading(true);
        const response = await adminService.getTenants();
        
        if (response.success && response.data) {
          setTenants(response.data);
        }
      } catch (err) {
        console.error('Error fetching tenants:', err);
        setError('Failed to load tenant clinics');
      } finally {
        setTenantLoading(false);
      }
    };

    fetchTenants();
  }, []);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};

    if (!formData.tenantId) {
      errors.tenantId = 'Please select a tenant clinic';
    }

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^\S+@\S+\.\S+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = 'Please confirm password';
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
const handleSubmit = async (e) => {
  e.preventDefault();
  
  // Validate form
  if (!validateForm()) {
    return;
  }

  setLoading(true);
  setError(null);

  try {
    // Prepare patient data
    const patientData = {
      tenantId: formData.tenantId,
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.toLowerCase().trim(),
      password: formData.password,
      role: 'patient',
      accountStatus: 'active',
      isEmailVerified: true,
      isVerified: true,
      onboardingCompleted: false
    };

    console.log('Creating patient:', patientData);

    const response = await adminService.createPatient(patientData);

    if (response.success) {
      alert('Patient created successfully!');
      navigate(`/admin/patients/${response.data._id}?tenantId=${formData.tenantId}`);
    } else {
      throw new Error(response.message || 'Failed to create patient');
    }
  } catch (err) {
    console.error('Error creating patient:', err);
    
    // Extract user-friendly error message
    let errorMessage = 'An error occurred while creating the patient';
    
    if (err.response?.data?.message) {
      // Backend returned a specific error message
      errorMessage = err.response.data.message;
    } else if (err.message) {
      errorMessage = err.message;
    }
    
    setError(errorMessage);
  } finally {
    setLoading(false);
  }
};

  // Handle cancel
  const handleCancel = () => {
    navigate('/admin/users');
  };

  return (
    <div className="admin-patient-details-container">
      {/* Header */}
      <div className="admin-patient-header">
        <div className="admin-header-left">
          <button className="admin-back-button" onClick={handleCancel}>
            <span className="admin-back-icon">←</span>
          </button>
          <div className="admin-patient-info">
            <h1 className="admin-patient-name">Add New Patient</h1>
            <p className="admin-patient-email">Create a new patient account</p>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="admin-error-container" style={{ marginBottom: '20px' }}>
          <div className="admin-error-alert">
            <span className="admin-error-icon">⚠️</span>
            <span className="admin-error-message">{error}</span>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="admin-tab-content">
        <div className="admin-tab-panel">
          <div className="admin-section-card">
            {tenantLoading ? (
              <div className="admin-loading-container">
                <div className="admin-loading-spinner"></div>
                <p>Loading tenant clinics...</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
  <div className="admin-add-personal-info-grid">
    {/* Tenant Selection - Full Width */}
    <div className="admin-detail-group admin-full-width">
      <label className="admin-detail-label">
        Tenant Clinic <span style={{ color: 'red' }}>*</span>
      </label>
      <select
        name="tenantId"
        value={formData.tenantId}
        onChange={handleChange}
        className="admin-form-input"
        required
      >
        <option value="">-- Select Tenant Clinic --</option>
        {tenants.map(tenant => (
          <option key={tenant._id} value={tenant._id}>
            {tenant.name}
          </option>
        ))}
      </select>
      {validationErrors.tenantId && (
        <span className="admin-error-text">{validationErrors.tenantId}</span>
      )}
    </div>

    {/* First Name - Left Column */}
    <div className="admin-detail-group">
      <label className="admin-detail-label">
        First Name <span style={{ color: 'red' }}>*</span>
      </label>
      <input
        type="text"
        name="firstName"
        value={formData.firstName}
        onChange={handleChange}
        className="admin-form-input"
        placeholder="Enter first name"
        required
      />
      {validationErrors.firstName && (
        <span className="admin-error-text">{validationErrors.firstName}</span>
      )}
    </div>

    {/* Last Name - Right Column */}
    <div className="admin-detail-group">
      <label className="admin-detail-label">
        Last Name <span style={{ color: 'red' }}>*</span>
      </label>
      <input
        type="text"
        name="lastName"
        value={formData.lastName}
        onChange={handleChange}
        className="admin-form-input"
        placeholder="Enter last name"
        required
      />
      {validationErrors.lastName && (
        <span className="admin-error-text">{validationErrors.lastName}</span>
      )}
    </div>

    {/* Email - Full Width */}
    <div className="admin-detail-group admin-full-width">
      <label className="admin-detail-label">
        Email Address <span style={{ color: 'red' }}>*</span>
      </label>
      <input
        type="email"
        name="email"
        value={formData.email}
        onChange={handleChange}
        className="admin-form-input"
        placeholder="patient@example.com"
        required
      />
      {validationErrors.email && (
        <span className="admin-error-text">{validationErrors.email}</span>
      )}
    </div>

    {/* Password - Left Column */}
    <div className="admin-detail-group">
      <label className="admin-detail-label">
        Password <span style={{ color: 'red' }}>*</span>
      </label>
      <input
        type="password"
        name="password"
        value={formData.password}
        onChange={handleChange}
        className="admin-form-input"
        placeholder="Minimum 8 characters"
        minLength={8}
        required
      />
      {validationErrors.password && (
        <span className="admin-error-text">{validationErrors.password}</span>
      )}
    </div>

    {/* Confirm Password - Right Column */}
    <div className="admin-detail-group">
      <label className="admin-detail-label">
        Confirm Password <span style={{ color: 'red' }}>*</span>
      </label>
      <input
        type="password"
        name="confirmPassword"
        value={formData.confirmPassword}
        onChange={handleChange}
        className="admin-form-input"
        placeholder="Re-enter password"
        required
      />
      {validationErrors.confirmPassword && (
        <span className="admin-error-text">{validationErrors.confirmPassword}</span>
      )}
    </div>
  </div>
  {/* Form Actions */}
<div style={{
  gridColumn: '1 / -1',
  display: 'flex',
  gap: '10px',
  marginTop: '30px',
  justifyContent: 'flex-end',
  width: '100%'
}}>
  <button
    type="button"
    onClick={handleCancel}
    className="admin-add-secondary-button"
    disabled={loading}
  >
    Cancel
  </button>
  <button
    type="submit"
    className="admin-add-primary-button"
    disabled={loading}
  >
    {loading ? 'Creating...' : 'Create Patient'}
  </button>
</div>
</form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPatientAdd;